import React, { useState } from 'react';
import ReactQuill from 'react-quill-new';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Ship, ChevronDown, ChevronUp, X, CheckCircle2 } from 'lucide-react';
import { formatCurrency, quillModules, quillFormats } from '../crmUtils';
import { CurrencyInput } from '../../ui/currency-input';

export const EmbarcacaoItemEditor = ({ item, index, onUpdate, onRemove, modelos }) => {
  const [expanded, setExpanded] = useState(true);
  const calcSub = () => {
    const base = (item.preco_base || 0) * (item.quantidade || 1);
    const up = (item.upgrades_selecionados || []).reduce((s, u) => s + (u.preco_adicional || 0), 0) * (item.quantidade || 1);
    const op = (item.opcionais_selecionados || []).reduce((s, o) => s + (o.preco || 0), 0) * (item.quantidade || 1);
    return base + up + op;
  };

  return (
    <div data-testid={`proposta-item-${index}`} className="border border-blue-200 rounded-lg bg-white">
      <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-t-lg">
        <Ship className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <div className="flex-1">
          <span className="font-semibold text-sm text-slate-800">{item.vessel_model_nome}</span>
          {item.vessel_model_codigo && <span className="text-xs text-slate-400 ml-2">({item.vessel_model_codigo})</span>}
        </div>
        <Input
          value={item.lote || ''}
          onChange={e => onUpdate(index, { ...item, lote: e.target.value.toUpperCase() })}
          placeholder="Lote (ex: LOTE 1)"
          className="h-6 text-[10px] w-28 text-center border-indigo-200"
        />
        <span className="text-sm font-bold text-blue-700">{formatCurrency(calcSub())}</span>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => onRemove(index)}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {expanded && (
        <div className="p-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Preço Base</Label>
              <CurrencyInput value={item.preco_base || 0} className="h-8 text-sm"
                onChange={v => onUpdate(index, { ...item, preco_base: v })} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Quantidade</Label>
              <Input type="number" min="1" value={item.quantidade || 1} className="h-8 text-sm"
                onChange={e => onUpdate(index, { ...item, quantidade: parseInt(e.target.value) || 1 })} />
            </div>
          </div>

          {item.itens_serie?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1.5">Itens Padrão (inclusos no preço base)</p>
              <div className="bg-slate-50 rounded-lg p-2 space-y-0.5">
                {item.itens_serie.map((s, i) => (
                  <div key={i} className="text-xs text-slate-600 flex items-center gap-2 py-0.5">
                    <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                    {s.grupo && <span className="text-slate-400 font-medium">[{s.grupo}]</span>}
                    <span>{s.descricao}</span>
                    {s.quantidade > 1 && <span className="text-slate-400">x{s.quantidade}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {item._available_upgrades?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700 mb-1.5">Upgrades Disponíveis</p>
              <div className="space-y-1">
                {item._available_upgrades.map((u, i) => {
                  const isSelected = (item.upgrades_selecionados || []).some(s => s.id === u.id);
                  return (
                    <div key={i} className={`flex items-center gap-2 text-xs rounded-lg p-2 cursor-pointer transition-colors ${isSelected ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                      onClick={() => {
                        const current = item.upgrades_selecionados || [];
                        const updated = isSelected
                          ? current.filter(s => s.id !== u.id)
                          : [...current, { id: u.id, descricao: u.descricao, substitui_descricao: u.substitui_descricao, preco_adicional: u.preco_adicional }];
                        onUpdate(index, { ...item, upgrades_selecionados: updated });
                      }}>
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <div className="flex-1">
                        <span className="text-slate-700 font-medium">{u.descricao}</span>
                        {u.substitui_descricao && <span className="text-slate-400 ml-1">(substitui: {u.substitui_descricao})</span>}
                      </div>
                      <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">+{formatCurrency(u.preco_adicional)}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {item._available_opcionais?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-blue-700 mb-1.5">Opcionais Disponíveis</p>
              <div className="space-y-1">
                {item._available_opcionais.map((o, i) => {
                  const isSelected = (item.opcionais_selecionados || []).some(s => s.id === o.id);
                  return (
                    <div key={i} className={`flex items-center gap-2 text-xs rounded-lg p-2 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                      onClick={() => {
                        const current = item.opcionais_selecionados || [];
                        const updated = isSelected
                          ? current.filter(s => s.id !== o.id)
                          : [...current, { id: o.id, descricao: o.descricao, preco: o.preco }];
                        onUpdate(index, { ...item, opcionais_selecionados: updated });
                      }}>
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <span className="text-slate-700 font-medium flex-1">{o.descricao}</span>
                      <Badge variant="outline" className="text-blue-600 border-blue-300 text-[10px]">+{formatCurrency(o.preco)}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-0.5">
              <Label className="text-xs text-slate-500">Especificações Técnicas (opcional)</Label>
              {(() => {
                const modelo = modelos?.find(m => m.id === item.vessel_model_id);
                const dims = modelo?.config_comercial?.dimensoes_gerais;
                return dims ? (
                  <button type="button" className="text-[10px] text-blue-600 hover:underline"
                    onClick={() => onUpdate(index, { ...item, specs_tecnicas: dims })}>
                    Usar dimensões de "{modelo.nome}"
                  </button>
                ) : null;
              })()}
            </div>
            <div className="quill-compact" data-testid={`specs-item-${index}`}>
              <ReactQuill theme="snow" value={item.specs_tecnicas || ''} onChange={v => onUpdate(index, { ...item, specs_tecnicas: v })}
                modules={quillModules} formats={quillFormats} placeholder="Ex: Comprimento: 22 pés / Boca: 2,50m / Calado: 0,45m..." />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
